import { ReactNode, useEffect } from 'react';
import { useConfig } from '@/hooks/useConfig/useConfig';
import { ConfigSchemaV1Type } from '@/providers/ConfigProvider/ConfigSchemaV1Type';
import { ConfigV1Type } from '@/providers/ConfigProvider/ConfigV1Type';
import { kebabize } from '@/utils/kebabize';
import { loadFonts } from '@/utils/loadFonts';

const FONT_BASE_PATH = '/client-config/fonts/';
/**
 * Sets theme variables on the document based on user's OS dark mode, and adds an onChange listener.
 */
function updateThemeProperties(
  isDarkModeEnabled: boolean,
  configSchema: ConfigSchemaV1Type,
  theme: ConfigV1Type['theme'],
  fontFamily: string,
) {
  (
    configSchema.properties.theme.properties.background.required as Array<
      keyof typeof theme.background
    >
  ).forEach((key) => {
    const colorMode = theme.background[key];
    if (colorMode) {
      document.documentElement.style.setProperty(
        `--bg-${kebabize(key)}`,
        isDarkModeEnabled ? colorMode.dark[0] : colorMode.light[0],
      );
    }
  });

  (
    configSchema.properties.theme.properties.text.required as Array<
      keyof typeof theme.text
    >
  ).forEach((key) => {
    if (key in theme.text) {
      document.documentElement.style.setProperty(
        `--text-${kebabize(key)}`,
        isDarkModeEnabled ? theme.text[key].dark[0] : theme.text[key].light[0],
      );
    }
  });

  // Set font-family CSS variable
  document.documentElement.style.setProperty('--font-family', fontFamily);
}

function MatchOSDarkModeOnFirstRender({ children }: { children: ReactNode }) {
  const { theme, configSchema } = useConfig();

  const isDarkModeEnabled = window.matchMedia(
    '(prefers-color-scheme: dark)',
  ).matches;

  // Load fonts dynamically based on config
  loadFonts(theme.fontFamily, FONT_BASE_PATH);

  updateThemeProperties(
    isDarkModeEnabled,
    configSchema,
    theme,
    theme.fontFamily,
  );

  return children;
}

function MatchOSDarkModeOnSubsequentRenders({
  children,
}: {
  children: ReactNode;
}) {
  const { theme, configSchema } = useConfig();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      updateThemeProperties(e.matches, configSchema, theme, theme.fontFamily);

    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme, configSchema]);

  return children;
}

/**
 * A wrapper component that sets the theme for the application.
 */
export function ThemeWrapper({ children }: { children: ReactNode }) {
  return (
    <MatchOSDarkModeOnFirstRender>
      <MatchOSDarkModeOnSubsequentRenders>
        {children}
      </MatchOSDarkModeOnSubsequentRenders>
    </MatchOSDarkModeOnFirstRender>
  );
}
